import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {MenuItem, MessageService, TreeNode} from 'primeng/api';
import {Menu} from 'primeng/menu';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {SelectionCriteriaService} from '../../service/api/selection-criteria.service';
import {SelectionTargetTypeEnum} from '../../enum/selection-target-type.enum';
import {LogicalOperatorEnum} from '../../enum/logical-operator.enum';
import {SetOperationEnum} from '../../enum/set-operation.enum';
import {ComparisonOperatorEnum} from '../../enum/comparison-operator.enum';
import {FieldTypeEnum} from '../../enum/field-type.enum';
import {
    AvailableField,
    AvailableFieldsResponse,
    FieldCondition,
    RuleGroupNode,
    SampleRecord,
    SelectionCriteriaTree,
    SelectionPreviewResponse,
    ValidationResult
} from '../../model/dto/api/selection-criteria-tree.interface';

interface RuleGroupUi extends RuleGroupNode {
    expanded: boolean;
    level: number;
    bgColor: string;
}

interface FieldConditionUi extends FieldCondition {
    displayValue: string;
    fieldDisplayName: string;
    operatorDisplayName: string;
}

@Component({
    selector: 'ct-selection-criteria-builder',
    templateUrl: './selection-criteria-builder.component.html',
    styleUrls: ['./selection-criteria-builder.component.scss'],
    standalone: false
})
export class SelectionCriteriaBuilderComponent implements OnInit, OnDestroy {

    @ViewChild('groupMenu') groupMenu!: Menu;
    @ViewChild('conditionMenu') conditionMenu!: Menu;

    @Input() campaignId: number;
    @Input() targetType: SelectionTargetTypeEnum = SelectionTargetTypeEnum.customer;
    @Input() readOnly: boolean = false;

    @Output() onSave = new EventEmitter<SelectionCriteriaTree>();
    @Output() onChange = new EventEmitter<SelectionCriteriaTree>();
    @Output() onValidation = new EventEmitter<ValidationResult>();
    @Output() onPreview = new EventEmitter<SelectionPreviewResponse>();

    // State
    criteriaTree: SelectionCriteriaTree;
    availableFields: AvailableFieldsResponse;
    validationResult: ValidationResult;
    previewResult: SelectionPreviewResponse;

    // UI State
    busy: boolean = false;
    initialized: boolean = false;
    selectedGroup: RuleGroupUi;
    selectedCondition: FieldConditionUi;

    // Tree Nodes for field selection
    fieldTreeNodes: TreeNode[] = [];

    // Menu items
    groupMenuItems: MenuItem[] = [];
    conditionMenuItems: MenuItem[] = [];
    logicalOperatorItems: MenuItem[] = [];
    setOperationItems: MenuItem[] = [];

    splitButtonOptions: MenuItem[] = [
        {label: 'Speichern', icon: 'fa-solid fa-save', command: () => this.saveCriteria()},
        {label: 'Speichern & Version erstellen', icon: 'fa-solid fa-code-branch', command: () => this.saveCriteria(true, 'Manual version snapshot')}
    ];
    // Level colors for visual hierarchy
    levelColors: { [level: number]: string } = {
        0: 'surface-ground',
        1: 'surface-100',
        2: 'surface-200',
        3: 'surface-300',
        4: 'surface-400'
    };

    // Enums for template
    LogicalOperatorEnum = LogicalOperatorEnum;
    SetOperationEnum = SetOperationEnum;
    ComparisonOperatorEnum = ComparisonOperatorEnum;
    FieldTypeEnum = FieldTypeEnum;
    SelectionTargetTypeEnum = SelectionTargetTypeEnum;

    private subscriptions: Subscription[] = [];

    // Sample record detail dialog state
    showSampleDetailDialog: boolean = false;
    selectedSampleRecord: SampleRecord;
    sampleDetailHeader: string = 'Datensatzdetails';

    // Version history dialog state (F-016)
    showVersionHistory: boolean = false;

    constructor(
        private selectionCriteriaService: SelectionCriteriaService,
        private messageService: MessageService,
        private router: Router
    ) {
    }

    ngOnInit(): void {
        this.initMenuItems();
        this.loadAvailableFields();
        this.loadCriteriaTree();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private initMenuItems(): void {
        this.logicalOperatorItems = [
            {
                label: 'AND',
                icon: 'fa-solid fa-link',
                command: () => this.setLogicalOperator(LogicalOperatorEnum.and)
            },
            {
                label: 'OR',
                icon: 'fa-solid fa-code-branch',
                command: () => this.setLogicalOperator(LogicalOperatorEnum.or)
            }
        ];

        this.setOperationItems = [
            {
                label: 'Vereinigung (Hinzufügen)',
                icon: 'fa-solid fa-object-group',
                command: () => this.setSetOperation(SetOperationEnum.union)
            },
            {
                label: 'Schnittmenge',
                icon: 'fa-solid fa-object-ungroup',
                command: () => this.setSetOperation(SetOperationEnum.intersect)
            },
            {
                label: 'Ausnahme (Entfernen)',
                icon: 'fa-solid fa-minus',
                command: () => this.setSetOperation(SetOperationEnum.except)
            }
        ];

        this.groupMenuItems = [
            {
                label: 'UND-Bedingung hinzufügen',
                icon: 'fa-solid fa-plus',
                command: () => this.addConditionToGroup(this.selectedGroup)
            },
            {
                label: 'Verschachtelte Gruppe hinzufügen',
                icon: 'fa-solid fa-layer-group',
                command: () => this.addNestedGroup(this.selectedGroup)
            },
            {separator: true},
            {
                label: 'Gruppe löschen',
                icon: 'fa-solid fa-trash',
                styleClass: 'text-red-500',
                command: () => this.deleteGroup(this.selectedGroup)
            }
        ];

        this.conditionMenuItems = [
            {
                label: 'Bedingung löschen',
                icon: 'fa-solid fa-trash',
                styleClass: 'text-red-500',
                command: () => this.deleteCondition(this.selectedCondition)
            }
        ];
    }

    private loadAvailableFields(): void {
        this.busy = true;
        const sub = this.selectionCriteriaService.getAvailableFields(this.targetType).subscribe({
            next: (response) => {
                this.availableFields = response;
                this.buildFieldTreeNodes();
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Verfügbare Felder konnten nicht geladen werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    private loadCriteriaTree(): void {
        if (!this.campaignId) return;

        this.busy = true;
        const sub = this.selectionCriteriaService.getOrCreateCriteriaTree(this.campaignId).subscribe({
            next: (tree) => {
                this.criteriaTree = tree;
                this.initialized = true;
                this.busy = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Selektionskriterien konnten nicht geladen werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    private buildFieldTreeNodes(): void {
        if (!this.availableFields) return;

        const nodesByCategory: { [key: string]: TreeNode } = {};

        for (const [category, fields] of Object.entries(this.availableFields.fieldsByCategory)) {
            const categoryNode: TreeNode = {
                label: category,
                data: {type: 'category', category},
                selectable: false,
                children: fields.map(field => ({
                    label: field.displayName,
                    data: field,
                    selectable: true,
                    icon: this.getFieldTypeIcon(field.fieldType)
                }))
            };
            nodesByCategory[category] = categoryNode;
        }

        this.fieldTreeNodes = Object.values(nodesByCategory);
    }

    private getFieldTypeIcon(fieldType: FieldTypeEnum): string {
        switch (fieldType) {
            case FieldTypeEnum.string:
                return 'fa-solid fa-font';
            case FieldTypeEnum.integer:
            case FieldTypeEnum.decimal:
                return 'fa-solid fa-hashtag';
            case FieldTypeEnum.date:
            case FieldTypeEnum.datetime:
                return 'fa-solid fa-calendar';
            case FieldTypeEnum.boolean:
                return 'fa-solid fa-toggle-on';
            case FieldTypeEnum.enum:
                return 'fa-solid fa-list';
            default:
                return 'fa-solid fa-question';
        }
    }

    // Group Operations

    addRootGroup(): void {
        if (this.readOnly || !this.criteriaTree) return;

        const newGroup: RuleGroupNode = {
            selectionCriteriaId: this.criteriaTree.id,
            logicalOperator: LogicalOperatorEnum.and,
            groupOrder: this.criteriaTree.rootGroups.length,
            isNegated: false,
            conditions: [],
            childGroups: []
        };

        if (this.criteriaTree.rootGroups.length > 0) {
            newGroup.setOperation = SetOperationEnum.union;
        }

        this.busy = true;
        const sub = this.selectionCriteriaService.addRuleGroup(
            this.criteriaTree.id,
            newGroup,
            null
        ).subscribe({
            next: (created) => {
                this.criteriaTree.rootGroups.push({
                    ...created,
                    conditions: [],
                    childGroups: []
                });
                this.handleChange();
                this.busy = false;
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Regelgruppe konnte nicht hinzugefügt werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    addNestedGroup(parentGroup: RuleGroupUi): void {
        if (this.readOnly || !parentGroup) return;

        const newGroup: RuleGroupNode = {
            selectionCriteriaId: this.criteriaTree.id,
            parentGroupId: parentGroup.id,
            logicalOperator: LogicalOperatorEnum.and,
            groupOrder: parentGroup.childGroups?.length || 0,
            isNegated: false,
            conditions: [],
            childGroups: []
        };

        this.busy = true;
        const sub = this.selectionCriteriaService.addRuleGroup(
            this.criteriaTree.id,
            newGroup,
            parentGroup.id
        ).subscribe({
            next: (created) => {
                if (!parentGroup.childGroups) {
                    parentGroup.childGroups = [];
                }
                parentGroup.childGroups.push({
                    ...created,
                    conditions: [],
                    childGroups: []
                });
                this.handleChange();
                this.busy = false;
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Verschachtelte Gruppe konnte nicht hinzugefügt werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    updateGroup(group: RuleGroupUi | RuleGroupNode): void {
        if (this.readOnly || !group.id) return;

        this.busy = true;
        const sub = this.selectionCriteriaService.updateRuleGroup(group.id, group).subscribe({
            next: (updated) => {
                Object.assign(group, updated);
                this.handleChange();
                this.busy = false;
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Regelgruppe konnte nicht aktualisiert werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    deleteGroup(group: RuleGroupUi): void {
        if (this.readOnly || !group.id) return;

        this.busy = true;
        const sub = this.selectionCriteriaService.deleteRuleGroup(group.id).subscribe({
            next: () => {
                this.removeGroupFromTree(group);
                this.handleChange();
                this.busy = false;
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Regelgruppe konnte nicht gelöscht werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    private removeGroupFromTree(group: RuleGroupUi): void {
        const removeFromList = (list: RuleGroupNode[]): boolean => {
            const index = list.findIndex(g => g.id === group.id);
            if (index !== -1) {
                list.splice(index, 1);
                return true;
            }
            for (const g of list) {
                if (g.childGroups && removeFromList(g.childGroups)) {
                    return true;
                }
            }
            return false;
        };

        removeFromList(this.criteriaTree.rootGroups);
    }

    // Condition Operations

    addConditionToGroup(group: RuleGroupUi): void {
        if (this.readOnly || !group.id) return;

        const newCondition: Partial<FieldCondition> = {
            ruleGroupId: group.id,
            fieldPath: '',
            comparisonOperator: ComparisonOperatorEnum.equals,
            conditionOrder: group.conditions?.length || 0,
            isNegated: false
        };

        this.busy = true;
        const sub = this.selectionCriteriaService.addFieldCondition(group.id, newCondition).subscribe({
            next: (created) => {
                if (!group.conditions) {
                    group.conditions = [];
                }
                group.conditions.push(created);
                this.handleChange();
                this.busy = false;
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Bedingung konnte nicht hinzugefügt werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    updateCondition(condition: FieldConditionUi): void {
        if (this.readOnly || !condition.id) return;

        this.busy = true;
        const sub = this.selectionCriteriaService.updateFieldCondition(condition.id, condition).subscribe({
            next: (updated) => {
                Object.assign(condition, updated);
                this.handleChange();
                this.busy = false;
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Bedingung konnte nicht aktualisiert werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    /**
     * Handles Enter key press in the value list autocomplete to add free-text values.
     * Since p-autoComplete with multiple mode is used instead of p-chips in PrimeNG v20,
     * we need to handle manual value entry.
     */
    onValueListKeyUp(event: KeyboardEvent, condition: FieldConditionUi): void {
        if (event.key === 'Enter') {
            const input = event.target as HTMLInputElement;
            const value = input.value?.trim();
            if (value) {
                if (!condition.conditionValueList) {
                    condition.conditionValueList = [];
                }
                if (!condition.conditionValueList.includes(value)) {
                    condition.conditionValueList.push(value);
                    input.value = '';
                    this.updateCondition(condition);
                }
            }
        }
    }

    deleteCondition(condition: FieldConditionUi): void {
        if (this.readOnly || !condition.id) return;

        this.busy = true;
        const sub = this.selectionCriteriaService.deleteFieldCondition(condition.id).subscribe({
            next: () => {
                this.removeConditionFromTree(condition);
                this.handleChange();
                this.busy = false;
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Bedingung konnte nicht gelöscht werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    private removeConditionFromTree(condition: FieldConditionUi): void {
        const removeFromGroup = (groups: RuleGroupNode[]): boolean => {
            for (const group of groups) {
                if (group.conditions) {
                    const index = group.conditions.findIndex(c => c.id === condition.id);
                    if (index !== -1) {
                        group.conditions.splice(index, 1);
                        return true;
                    }
                }
                if (group.childGroups && removeFromGroup(group.childGroups)) {
                    return true;
                }
            }
            return false;
        };

        removeFromGroup(this.criteriaTree.rootGroups);
    }

    // Field Selection

    onFieldSelect(condition: FieldConditionUi, event: any): void {
        if (event.node?.data && event.node.data.fieldPath) {
            const field: AvailableField = event.node.data;
            condition.fieldPath = field.fieldPath;
            condition.fieldType = field.fieldType;
            condition.fieldSourceEntity = field.fieldSourceEntity;
            condition.conditionValue = '';
            condition.conditionValueSecondary = '';
            condition.conditionValueList = [];

            // Set default operator based on field type
            if (field.allowedOperators?.length > 0) {
                condition.comparisonOperator = field.allowedOperators[0];
            }

            this.updateCondition(condition);
        }
    }

    getFieldByPath(fieldPath: string): AvailableField | undefined {
        return this.availableFields?.fields.find(f => f.fieldPath === fieldPath);
    }

    // Operator Helpers

    setLogicalOperator(operator: LogicalOperatorEnum): void {
        if (this.selectedGroup) {
            this.selectedGroup.logicalOperator = operator;
            this.updateGroup(this.selectedGroup);
        }
    }

    setSetOperation(operation: SetOperationEnum): void {
        if (this.selectedGroup) {
            this.selectedGroup.setOperation = operation;
            this.updateGroup(this.selectedGroup);
        }
    }

    getLogicalOperatorLabel(operator: LogicalOperatorEnum): string {
        return operator === LogicalOperatorEnum.and ? 'UND' : 'ODER';
    }

    getSetOperationLabel(operation: SetOperationEnum): string {
        switch (operation) {
            case SetOperationEnum.union:
                return 'Vereinigung';
            case SetOperationEnum.intersect:
                return 'Schnittmenge';
            case SetOperationEnum.except:
                return 'Ausnahme';
            default:
                return '';
        }
    }

    getOperatorLabel(operator: ComparisonOperatorEnum): string {
        const labels: { [key: number]: string } = {
            [ComparisonOperatorEnum.equals]: '=',
            [ComparisonOperatorEnum.notEquals]: '!=',
            [ComparisonOperatorEnum.greaterThan]: '>',
            [ComparisonOperatorEnum.greaterThanOrEquals]: '>=',
            [ComparisonOperatorEnum.lessThan]: '<',
            [ComparisonOperatorEnum.lessThanOrEquals]: '<=',
            [ComparisonOperatorEnum.between]: 'zwischen',
            [ComparisonOperatorEnum.in]: 'in',
            [ComparisonOperatorEnum.notIn]: 'nicht in',
            [ComparisonOperatorEnum.contains]: 'enthält',
            [ComparisonOperatorEnum.startsWith]: 'beginnt mit',
            [ComparisonOperatorEnum.endsWith]: 'endet mit',
            [ComparisonOperatorEnum.isNull]: 'ist leer',
            [ComparisonOperatorEnum.isNotNull]: 'ist nicht leer'
        };
        return labels[operator] || String(operator);
    }

    getAllowedOperators(condition: FieldConditionUi): ComparisonOperatorEnum[] {
        const field = this.getFieldByPath(condition.fieldPath);
        return field?.allowedOperators || Object.values(ComparisonOperatorEnum)
            .filter(v => typeof v === 'number') as ComparisonOperatorEnum[];
    }

    getEnumValues(condition: FieldConditionUi): string[] {
        const field = this.getFieldByPath(condition.fieldPath);
        return field?.enumValues || [];
    }

    needsValue(operator: ComparisonOperatorEnum): boolean {
        return operator !== ComparisonOperatorEnum.isNull && operator !== ComparisonOperatorEnum.isNotNull;
    }

    needsSecondValue(operator: ComparisonOperatorEnum): boolean {
        return operator === ComparisonOperatorEnum.between;
    }

    needsValueList(operator: ComparisonOperatorEnum): boolean {
        return operator === ComparisonOperatorEnum.in || operator === ComparisonOperatorEnum.notIn;
    }

    // Menu Handlers

    showGroupMenu(event: Event, group: RuleGroupUi): void {
        this.selectedGroup = group;
        this.groupMenu.toggle(event);
    }

    showConditionMenu(event: Event, condition: FieldConditionUi): void {
        this.selectedCondition = condition;
        this.conditionMenu.toggle(event);
    }

    // Preview and Validation

    executePreview(): void {
        if (!this.criteriaTree?.id) return;

        this.busy = true;
        const sub = this.selectionCriteriaService.executePreview(this.criteriaTree.id, {
            selectionCriteriaId: this.criteriaTree.id,
            sampleSize: 10,
            includeCount: true,
            includeSamples: true
        }).subscribe({
            next: (response) => {
                this.previewResult = response;
                this.onPreview.emit(response);
                this.busy = false;

                if (response.success) {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Vorschau abgeschlossen',
                        detail: `${response.estimatedCount} übereinstimmende Datensätze gefunden`
                    });
                } else {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Vorschau fehlgeschlagen',
                        detail: response.errorMessage
                    });
                }
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Vorschau konnte nicht ausgeführt werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    validateCriteria(): void {
        if (!this.criteriaTree?.id) return;

        this.busy = true;
        const sub = this.selectionCriteriaService.validateCriteria(this.criteriaTree.id).subscribe({
            next: (result) => {
                this.validationResult = result;
                this.onValidation.emit(result);
                this.busy = false;

                if (result.isValid) {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Validierung bestanden',
                        detail: 'Selektionskriterien sind gültig'
                    });
                } else {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Validierungsprobleme',
                        detail: `${result.errors.length} Fehler gefunden`
                    });
                }
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Kriterien konnten nicht validiert werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    // Save

    saveCriteria(createVersion: boolean = false, versionNotes?: string): void {
        if (this.readOnly || !this.criteriaTree) return;

        this.busy = true;
        const sub = this.selectionCriteriaService.saveCriteriaTree({
            criteriaTree: this.criteriaTree,
            createNewVersion: createVersion,
            versionNotes: versionNotes
        }).subscribe({
            next: (saved) => {
                this.criteriaTree = saved;
                this.onSave.emit(saved);
                this.busy = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Gespeichert',
                    detail: createVersion
                        ? 'Selektionskriterien gespeichert und neue Version erstellt'
                        : 'Selektionskriterien erfolgreich gespeichert'
                });
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Fehler',
                    detail: 'Selektionskriterien konnten nicht gespeichert werden'
                });
                this.busy = false;
            }
        });
        this.subscriptions.push(sub);
    }

    /**
     * Opens the version history panel.
     * F-016: Selection Criteria Versioning
     */
    openVersionHistory(): void {
        if (this.criteriaTree?.id) {
            this.showVersionHistory = true;
        }
    }

    /**
     * Handles rollback completion from version history component.
     * Reloads the criteria tree to reflect the restored version.
     */
    onVersionRollback(): void {
        this.loadCriteriaTree();
    }

    private handleChange(): void {
        this.onChange.emit(this.criteriaTree);
    }

    // UI Helpers

    getLevelColor(level: number): string {
        return this.levelColors[Math.min(level, 4)] || 'surface-ground';
    }

    trackByGroupId(index: number, group: RuleGroupNode): number | undefined {
        return group.id;
    }

    trackByConditionId(index: number, condition: FieldCondition): number | undefined {
        return condition.id;
    }

    // Sample Record Detail Methods

    onSampleRecordSelect(event: any): void {
        if (event?.data) {
            this.viewSampleRecordDetails(event.data);
        }
    }

    viewSampleRecordDetails(sample: SampleRecord, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }

        this.selectedSampleRecord = sample;
        this.sampleDetailHeader = this.targetType === SelectionTargetTypeEnum.customer
            ? `Kundendetails - ${sample.customerName || 'ID: ' + sample.customerId}`
            : `Vertragsdetails - ${sample.contractNumber || 'ID: ' + sample.contractId}`;
        this.showSampleDetailDialog = true;
    }

    getSampleDataFields(sample: SampleRecord): Array<{ key: string; value: string }> {
        if (!sample?.data) {
            return [];
        }

        return Object.entries(sample.data).map(([key, value]) => ({
            key: key,
            value: value != null ? String(value) : 'N/A'
        }));
    }

    navigateToFullRecord(sample: SampleRecord): void {
        this.showSampleDetailDialog = false;

        if (sample.entityAlias && sample.objectId) {
            const entityPath = sample.entityAlias.replace('.', '/').toLowerCase();
            this.router.navigate([`/${entityPath}/${sample.objectId}`]);
        } else if (this.targetType === SelectionTargetTypeEnum.customer && sample.customerId) {
            this.router.navigate(['/cr/customer', sample.customerId]);
        } else if (this.targetType === SelectionTargetTypeEnum.contract && sample.contractId) {
            this.router.navigate(['/cm/contract', sample.contractId]);
        }
    }
}
